import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaFilePdf, FaUpload, FaArrowRight, FaRedo } from 'react-icons/fa';
import { uploadPlan } from '../../../../services/onboardingWizardService';

function Step1UploadPlan({ session, propertyId, onApplyBundle, onAdvance }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(session.title || '');
  const [levelName, setLevelName] = useState(session.level_name || '');
  const [notes, setNotes] = useState(session.notes || '');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const alreadyUploaded = Boolean(session.pinpoint_document_id);

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) pickFile(dropped);
  };
  const onPick = (e) => { const f = e.target.files?.[0]; if (f) pickFile(f); };
  const pickFile = (f) => {
    if (!/\.pdf$/i.test(f.name)) return toast.error('Floor plan must be a PDF');
    if (f.size > 25 * 1024 * 1024) return toast.error('File exceeds 25 MB limit');
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const doUpload = async () => {
    if (!file) return toast.error('Select a PDF first');
    setUploading(true);
    try {
      const bundle = await uploadPlan(propertyId, session.id, file, { title, level_name: levelName, notes });
      onApplyBundle(bundle);
      toast.success('Plan uploaded');
      onAdvance(2);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const goToStep2 = () => onAdvance(2);

  return (
    <div className="ow-step-body">
      <section className="ow-card">
        <header className="ow-card-head">
          <div>
            <div className="ow-card-kicker">Step 1 · Upload plan</div>
            <h2 className="ow-card-title">Drop the floor-plan PDF</h2>
            <p className="ow-card-sub">A single PDF, up to 25 MB. You can retitle and add level/notes before uploading.</p>
          </div>
        </header>

        {alreadyUploaded ? (
          <div className="ow-uploaded">
            <FaFilePdf className="ow-uploaded-icon" />
            <div className="ow-uploaded-copy">
              <strong>{session.title || 'Floor plan'}</strong>
              <span>{session.level_name || '—'}{session.notes ? ` · ${session.notes}` : ''}</span>
            </div>
            <div className="ow-uploaded-actions">
              <button className="ow-btn ow-btn-ghost" onClick={() => fileRef.current?.click()}>
                <FaRedo /> Replace file
              </button>
              <button className="ow-btn ow-btn-primary" onClick={goToStep2}>
                Continue <FaArrowRight />
              </button>
            </div>
            <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={onPick} />
          </div>
        ) : (
          <>
            <div
              className={`ow-dropzone ${dragging ? 'is-dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={onPick} />
              {file ? (
                <div className="ow-dropzone-file">
                  <FaFilePdf />
                  <div>
                    <strong>{file.name}</strong>
                    <span>{(file.size / 1024 / 1024).toFixed(2)} MB · ready to upload</span>
                  </div>
                </div>
              ) : (
                <div className="ow-dropzone-empty">
                  <FaUpload className="ow-dropzone-icon" />
                  <strong>Drag a PDF here, or click to browse</strong>
                  <span>Floor plans, roof layouts, or elevation drawings.</span>
                </div>
              )}
            </div>

            <div className="ow-form-grid">
              <div>
                <label className="ow-label">Plan title</label>
                <input className="ow-input" value={title} onChange={e => setTitle(e.target.value)}
                       placeholder="e.g. Level 4 roof layout" />
              </div>
              <div>
                <label className="ow-label">Floor / level</label>
                <input className="ow-input" value={levelName} onChange={e => setLevelName(e.target.value)}
                       placeholder="e.g. Roof, Level 3" />
              </div>
              <div className="ow-form-wide">
                <label className="ow-label">Notes (optional)</label>
                <textarea className="ow-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                          placeholder="Context anyone picking up this draft should know." />
              </div>
            </div>

            <footer className="ow-card-foot">
              <button className="ow-btn ow-btn-primary ow-btn-lg"
                      disabled={!file || uploading}
                      onClick={doUpload}>
                {uploading ? 'Uploading…' : (<><FaUpload /> Upload and continue</>)}
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

export default Step1UploadPlan;
